import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  STORAGE_KEY,
  createTeam,
  updateTeamsFromMatch,
  getClassifiedTeams,
  buildKnockoutMatches,
  buildSemifinalMatches,
  getWinnerFromSemifinals,
} from '../utils/tournament';

const EMPTY_TOURNAMENT_STATE = {
  groups: [],
  matches: [],
  knockoutMatches: [],
  semifinalMatches: [],
  winner: null,
};

const getInitialTournamentState = () => {
  try {
    const savedState = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
    if (!savedState) {
      return EMPTY_TOURNAMENT_STATE;
    }

    const parsedState = JSON.parse(savedState);
    return {
      groups: parsedState?.groups || [],
      matches: parsedState?.matches || [],
      knockoutMatches: parsedState?.knockoutMatches || [],
      semifinalMatches: parsedState?.semifinalMatches || [],
      winner: parsedState?.winner || null,
    };
  } catch (error) {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return EMPTY_TOURNAMENT_STATE;
  }
};

export const useTournament = () => {
  const initialTournamentState = useMemo(() => getInitialTournamentState(), []);
  const [groups, setGroups] = useState(initialTournamentState.groups);
  const [matches, setMatches] = useState(initialTournamentState.matches);
  const [knockoutMatches, setKnockoutMatches] = useState(initialTournamentState.knockoutMatches);
  const [semifinalMatches, setSemifinalMatches] = useState(initialTournamentState.semifinalMatches);
  const [winner, setWinner] = useState(initialTournamentState.winner);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ groups, matches, knockoutMatches, semifinalMatches, winner })
    );
  }, [groups, matches, knockoutMatches, semifinalMatches, winner]);

  const resetTournament = useCallback(() => {
    setGroups([]);
    setMatches([]);
    setKnockoutMatches([]);
    setSemifinalMatches([]);
    setWinner(null);
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const addGroup = useCallback((groupName) => {
    const normalizedName = groupName.trim().toUpperCase();
    if (!normalizedName) {
      return;
    }

    setGroups((previousGroups) => {
      const alreadyExists = previousGroups.some((group) => group.name === normalizedName);
      if (alreadyExists) {
        return previousGroups;
      }
      return [...previousGroups, { name: normalizedName, teams: [] }];
    });
  }, []);

  const addTeam = useCallback((groupName, teamName) => {
    const normalizedTeamName = teamName.trim();
    if (!groupName || !normalizedTeamName) {
      return;
    }

    setGroups((previousGroups) =>
      previousGroups.map((group) =>
        group.name === groupName
          ? {
              ...group,
              teams: group.teams.some((team) => team.name.toLowerCase() === normalizedTeamName.toLowerCase())
                ? group.teams
                : [...group.teams, createTeam(normalizedTeamName, group.teams.length)],
            }
          : group
      )
    );
  }, []);

  const addMatch = useCallback((match) => {
    setMatches((previousMatches) => [...previousMatches, match]);

    setGroups((previousGroups) =>
      previousGroups.map((group) => {
        const hasBothTeams = group.teams.some((team) => team.name === match.team1)
          && group.teams.some((team) => team.name === match.team2);

        if (!hasBothTeams) {
          return group;
        }

        return {
          ...group,
          teams: updateTeamsFromMatch(group.teams, match),
        };
      })
    );
  }, []);

  const groupNames = useMemo(() => groups.map((group) => group.name), [groups]);

  const classifiedTeams = useMemo(() => getClassifiedTeams(groups), [groups]);

  const createKnockoutPhase = useCallback(() => {
    setKnockoutMatches(buildKnockoutMatches(classifiedTeams));
    setSemifinalMatches([]);
    setWinner(null);
  }, [classifiedTeams]);

  const addKnockoutResult = useCallback((matchIndex, result) => {
    setKnockoutMatches((previousMatches) => {
      const updatedMatches = previousMatches.map((match, index) =>
        index === matchIndex ? { ...match, result } : match
      );

      setSemifinalMatches(buildSemifinalMatches(updatedMatches));
      return updatedMatches;
    });
  }, []);

  const addSemifinalResult = useCallback((matchIndex, result) => {
    setSemifinalMatches((previousMatches) => {
      const updatedMatches = previousMatches.map((match, index) =>
        index === matchIndex ? { ...match, result } : match
      );

      setWinner(getWinnerFromSemifinals(updatedMatches));
      return updatedMatches;
    });
  }, []);

  const canGenerateKnockout = useMemo(
    () => Object.values(classifiedTeams).some((teamsByGroup) => teamsByGroup.length === 2),
    [classifiedTeams]
  );

  return {
    groups,
    matches,
    knockoutMatches,
    semifinalMatches,
    winner,
    groupNames,
    classifiedTeams,
    canGenerateKnockout,
    resetTournament,
    addGroup,
    addTeam,
    addMatch,
    createKnockoutPhase,
    addKnockoutResult,
    addSemifinalResult,
  };
};
